/**
 * AI Observability events — Phase 4 narrow first cut.
 *
 * Local wrapper module that emits validator + launch-flow events
 * through the existing `useProductAnalytics().trackEvent` pipeline.
 *
 * Why local (and not in @dadohr/shared): the shared package's source
 * vs. published `.d.ts` for `ProductAnalyticsEventV2` are out of
 * sync (Beads new-main-mofi has the full discovery). Adding new
 * event types to shared would force a republish that ripples to
 * every consumer (apps + functions). For 7 FE-only events, we cast
 * `as unknown as ProductAnalyticsEvent` at the trackEvent boundary
 * and keep the type contracts strict INSIDE this module. The CF
 * accepts the payload shape regardless of the TS union.
 *
 * If we ever want strict types at the boundary, move these event
 * interfaces into shared/src/.../productAnalytics.ts, add them to
 * the `ProductAnalyticsEvent` union, republish shared, bump apps
 * dep, then remove the cast here.
 *
 * Privacy floor:
 *   - String fields truncated to 200 chars.
 *   - Email-pattern values stripped to `[email]`.
 *   - NEVER pass raw user-data (names, message bodies, subjects).
 *     Callers are responsible for choosing what to pass; this module
 *     defends only against accidental long strings + emails.
 *   - Numeric fields and IDs pass through as-is.
 *
 * Crash resistance:
 *   - Every emit wraps the underlying trackEvent in try/catch.
 *   - `trackEvent` itself is already non-throwing (the
 *     useProductAnalytics hook catches errors), but defense in depth
 *     means a future refactor of that hook can't accidentally crash
 *     callers from any of these helpers.
 */

import {
	PAEventType,
	type ProductAnalyticsEvent,
} from '@dadohr/shared/domainTypes/productAnalytics';
import { useCallback, useMemo } from 'react';
import { useProductAnalytics } from './index';

// `ProductAnalyticsEventV2` exists in the published `@dadohr/shared`
// .d.ts but NOT in source (see Beads new-main-mofi for the divergence
// detail). Importing it would add to the 4 pre-existing TS errors in
// apps. Skip — the trackEvent boundary cast uses `ProductAnalyticsEvent`
// alone, which the CF accepts regardless of TS union shape.

// ─────────────────────────────────────────────────────────────────────
// Privacy floor helpers
// ─────────────────────────────────────────────────────────────────────

const EMAIL_RE = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;

export const truncate200 = (s: unknown): string => {
	if (typeof s !== 'string') return '';
	const trimmed = s.length > 200 ? s.slice(0, 200) : s;
	return trimmed.replace(EMAIL_RE, '[email]');
};

const safeNumber = (n: unknown, fallback = 0): number =>
	typeof n === 'number' && Number.isFinite(n) ? n : fallback;

// ─────────────────────────────────────────────────────────────────────
// Event names — single source of truth. Exported for test assertions
// and for the Mixpanel dashboard owner to filter on.
// ─────────────────────────────────────────────────────────────────────

export const AI_OBSERVABILITY_EVENT = {
	// Phase 4 — Validator + launch flow (see Beads new-main-mofi)
	CHECK_CHANGES_CLICKED: 'Check Changes Clicked',
	CHECK_CHANGES_RESULT: 'Check Changes Result',
	LAUNCH_CLICKED: 'Launch Clicked',
	LAUNCH_BLOCKED_BY_REVALIDATION: 'Launch Blocked By Revalidation',
	LAUNCH_DISABLED_BY_EDIT: 'Launch Disabled By Edit',
	LAUNCH_READY_SHOWN: 'Launch Ready Shown',
	LAUNCH_READY_HELP_CLICKED: 'Launch Ready Help Clicked',
	// Phase 2 — Copilot conversation lifecycle (see Beads new-main-s437)
	COPILOT_CHAT_SESSION_STARTED: 'Copilot Chat Session Started',
	COPILOT_PROMPT_SENT: 'Copilot Prompt Sent',
	COPILOT_RESPONSE_RECEIVED: 'Copilot Response Received',
	COPILOT_TOOL_CALL: 'Copilot Tool Call',
	COPILOT_TOOL_ERROR: 'Copilot Tool Error',
	COPILOT_CHAT_SESSION_ENDED: 'Copilot Chat Session Ended',
	// Phase 4 followup — Heal-on-launch normalizers (see Beads followup)
	HEAL_ON_LAUNCH_APPLIED: 'Heal On Launch Applied',
} as const;

export type AIObservabilityEventName =
	(typeof AI_OBSERVABILITY_EVENT)[keyof typeof AI_OBSERVABILITY_EVENT];

// ─────────────────────────────────────────────────────────────────────
// Per-event prop typing — strict inside the module. Cast happens only
// at the trackEvent boundary.
// ─────────────────────────────────────────────────────────────────────

interface BasePayload {
	eventType: PAEventType.EXPERIENCE_TPL;
	'Experience Template ID': string;
	Explanation: string;
	name: AIObservabilityEventName;
}

export interface CheckChangesClickedProps {
	editSeqAtClick: number;
}
export interface CheckChangesResultProps {
	errorCount: number;
	durationMs: number;
}
export interface LaunchClickedProps {
	isLaunchReady: boolean;
}
export interface LaunchBlockedByRevalidationProps {
	errorCount: number;
}
export interface LaunchDisabledByEditProps {
	editsSinceLastCheck: number;
}

// Build the payload + cast at the boundary.
const buildPayload = (
	templateId: string,
	name: AIObservabilityEventName,
	explanation: string,
	extra: Record<string, unknown>
): ProductAnalyticsEvent => {
	const base: BasePayload = {
		eventType: PAEventType.EXPERIENCE_TPL,
		'Experience Template ID': truncate200(templateId),
		Explanation: explanation,
		name,
	};
	return { ...base, ...extra } as unknown as ProductAnalyticsEvent;
};

/**
 * Copilot-event payload builder. Copilot conversations can happen on
 * non-template pages (the copilot is global), so we use
 * `PAEventType.OTHER` and key on `Conversation ID` as the primary
 * correlator. `templateId` is optional context.
 */
const buildCopilotPayload = (
	conversationId: string,
	name: AIObservabilityEventName,
	explanation: string,
	extra: Record<string, unknown>,
	templateId?: string
): ProductAnalyticsEvent => {
	const base = {
		eventType: PAEventType.OTHER,
		'Conversation ID': truncate200(conversationId),
		Explanation: explanation,
		name,
		...(templateId
			? { 'Experience Template ID': truncate200(templateId) }
			: {}),
	};
	return { ...base, ...extra } as unknown as ProductAnalyticsEvent;
};

// ─────────────────────────────────────────────────────────────────────
// Copilot event prop types
// ─────────────────────────────────────────────────────────────────────

export type CopilotMode = 'edit' | 'create';
export type CopilotExitReason =
	| 'drawer-close'
	| 'nav-away'
	| 'org-switch'
	| 'unknown';

export interface CopilotSessionStartedProps {
	templateId?: string;
	pageContext?: string; // e.g. 'TemplateEdit', 'ExperienceList'
	mode: CopilotMode;
}
export interface CopilotPromptSentProps {
	messageLength: number;
	hasTemplateContext: boolean;
}
export interface CopilotResponseReceivedProps {
	modelLatencyMs: number;
	toolCallCount: number;
	tokensIn: number;
	tokensOut: number;
	totalDurationMs: number;
	templateId?: string;
}
export interface CopilotToolCallProps {
	toolName: string;
	success: boolean;
	durationMs: number;
}
export interface CopilotToolErrorProps {
	toolName: string;
	errorClass: string;
}
export interface CopilotSessionEndedProps {
	totalTurns: number;
	totalToolCalls: number;
	totalTokens: number;
	durationSec: number;
	exitReason: CopilotExitReason;
	templateId?: string;
}

export interface HealOnLaunchAppliedProps {
	/** Which normalize* function fired (e.g. 'normalizeSwagUpChoiceBlock').
	 * Matches the value passed to the normalizer's onHealed callback. */
	normalizerName: string;
	/** Count of blocks / actions touched in this heal pass. */
	itemsHealed: number;
	/** Surface that triggered the heal — auto-save vs. Check Changes. */
	trigger: 'auto-save' | 'check-changes';
}

// ─────────────────────────────────────────────────────────────────────
// Public hook — returns one emitter per event type. Each is wrapped
// in try/catch so a future regression in trackEvent can't crash the
// caller.
// ─────────────────────────────────────────────────────────────────────

export const useAIObservability = () => {
	const { trackEvent } = useProductAnalytics();

	const safeEmit = useCallback(
		(payload: ProductAnalyticsEvent): void => {
			try {
				// Fire-and-forget. trackEvent returns a promise but we
				// don't await it — callers shouldn't block on analytics.
				void trackEvent(payload).catch((err) => {
					// Defensive: trackEvent already catches internally, but
					// belt-and-suspenders in case of future drift.
					console.debug('[ai-observability] trackEvent rejected', err);
				});
			} catch (err) {
				console.debug('[ai-observability] emit threw', err);
			}
		},
		[trackEvent]
	);

	return useMemo(
		() => ({
			trackCheckChangesClicked: (
				templateId: string,
				props: CheckChangesClickedProps
			): void => {
				safeEmit(
					buildPayload(
						templateId,
						AI_OBSERVABILITY_EVENT.CHECK_CHANGES_CLICKED,
						'Admin clicked Check Changes to validate the template',
						{ 'Edit Seq At Click': safeNumber(props.editSeqAtClick) }
					)
				);
			},

			trackCheckChangesResult: (
				templateId: string,
				props: CheckChangesResultProps
			): void => {
				safeEmit(
					buildPayload(
						templateId,
						AI_OBSERVABILITY_EVENT.CHECK_CHANGES_RESULT,
						'Validation completed — errorCount + durationMs captured',
						{
							'Error Count': safeNumber(props.errorCount),
							'Duration Ms': safeNumber(props.durationMs),
						}
					)
				);
			},

			trackLaunchClicked: (templateId: string, props: LaunchClickedProps): void => {
				safeEmit(
					buildPayload(
						templateId,
						AI_OBSERVABILITY_EVENT.LAUNCH_CLICKED,
						'Admin clicked the Launch button (ROUGH_DRAFT mode)',
						{
							// `Is Launch Ready` should ALWAYS be true at click time
							// since the button is disabled otherwise. If it ever
							// arrives false, the UI gate was bypassed — investigate.
							'Is Launch Ready': !!props.isLaunchReady,
						}
					)
				);
			},

			trackLaunchBlockedByRevalidation: (
				templateId: string,
				props: LaunchBlockedByRevalidationProps
			): void => {
				safeEmit(
					buildPayload(
						templateId,
						AI_OBSERVABILITY_EVENT.LAUNCH_BLOCKED_BY_REVALIDATION,
						'Re-validation inside onLaunchFromRoughDraft found errors that were not present at last Check Changes — safety net fired',
						{ 'Error Count': safeNumber(props.errorCount) }
					)
				);
			},

			trackLaunchDisabledByEdit: (
				templateId: string,
				props: LaunchDisabledByEditProps
			): void => {
				safeEmit(
					buildPayload(
						templateId,
						AI_OBSERVABILITY_EVENT.LAUNCH_DISABLED_BY_EDIT,
						'Admin edited the template after a clean Check Changes; Launch re-disabled by the safety gate',
						{ 'Edits Since Last Check': safeNumber(props.editsSinceLastCheck, 1) }
					)
				);
			},

			trackLaunchReadyShown: (templateId: string): void => {
				safeEmit(
					buildPayload(
						templateId,
						AI_OBSERVABILITY_EVENT.LAUNCH_READY_SHOWN,
						'LaunchReadyMessage panel rendered — Check Changes returned clean and no edits since',
						{}
					)
				);
			},

			trackLaunchReadyHelpClicked: (templateId: string): void => {
				safeEmit(
					buildPayload(
						templateId,
						AI_OBSERVABILITY_EVENT.LAUNCH_READY_HELP_CLICKED,
						'Admin clicked the help@dadohr.com mailto link on the LaunchReadyMessage panel',
						{}
					)
				);
			},

			// ─────────────────────────────────────────────────────────
			// Phase 2 — Copilot conversation lifecycle
			// ─────────────────────────────────────────────────────────

			trackCopilotSessionStarted: (
				conversationId: string,
				props: CopilotSessionStartedProps
			): void => {
				safeEmit(
					buildCopilotPayload(
						conversationId,
						AI_OBSERVABILITY_EVENT.COPILOT_CHAT_SESSION_STARTED,
						'Copilot drawer opened',
						{
							Mode: truncate200(props.mode),
							'Page Context': truncate200(props.pageContext ?? ''),
						},
						props.templateId
					)
				);
			},

			trackCopilotPromptSent: (
				conversationId: string,
				props: CopilotPromptSentProps
			): void => {
				safeEmit(
					buildCopilotPayload(
						conversationId,
						AI_OBSERVABILITY_EVENT.COPILOT_PROMPT_SENT,
						'User sent a message to the copilot',
						{
							// Length only — NEVER send raw message content.
							'Message Length': safeNumber(props.messageLength),
							'Has Template Context': !!props.hasTemplateContext,
						}
					)
				);
			},

			trackCopilotResponseReceived: (
				conversationId: string,
				props: CopilotResponseReceivedProps
			): void => {
				safeEmit(
					buildCopilotPayload(
						conversationId,
						AI_OBSERVABILITY_EVENT.COPILOT_RESPONSE_RECEIVED,
						'Copilot returned a response',
						{
							'Model Latency Ms': safeNumber(props.modelLatencyMs),
							'Tool Call Count': safeNumber(props.toolCallCount),
							'Tokens In': safeNumber(props.tokensIn),
							'Tokens Out': safeNumber(props.tokensOut),
							'Total Duration Ms': safeNumber(props.totalDurationMs),
						},
						props.templateId
					)
				);
			},

			trackCopilotToolCall: (
				conversationId: string,
				props: CopilotToolCallProps
			): void => {
				safeEmit(
					buildCopilotPayload(
						conversationId,
						AI_OBSERVABILITY_EVENT.COPILOT_TOOL_CALL,
						'Copilot agent invoked a tool',
						{
							'Tool Name': truncate200(props.toolName),
							Success: !!props.success,
							'Duration Ms': safeNumber(props.durationMs),
						}
					)
				);
			},

			trackCopilotToolError: (
				conversationId: string,
				props: CopilotToolErrorProps
			): void => {
				safeEmit(
					buildCopilotPayload(
						conversationId,
						AI_OBSERVABILITY_EVENT.COPILOT_TOOL_ERROR,
						'Copilot tool returned an error',
						{
							'Tool Name': truncate200(props.toolName),
							// errorClass is a short non-PII tag — server-side
							// `classifyToolErrorClass` produced it. We never
							// ship the raw error message through analytics.
							'Error Class': truncate200(props.errorClass),
						}
					)
				);
			},

			trackCopilotSessionEnded: (
				conversationId: string,
				props: CopilotSessionEndedProps
			): void => {
				safeEmit(
					buildCopilotPayload(
						conversationId,
						AI_OBSERVABILITY_EVENT.COPILOT_CHAT_SESSION_ENDED,
						'Copilot drawer closed or admin navigated away',
						{
							'Total Turns': safeNumber(props.totalTurns),
							'Total Tool Calls': safeNumber(props.totalToolCalls),
							'Total Tokens': safeNumber(props.totalTokens),
							'Duration Sec': safeNumber(props.durationSec),
							'Exit Reason': truncate200(props.exitReason),
						},
						props.templateId
					)
				);
			},

			// ─────────────────────────────────────────────────────────
			// Phase 4 followup — Heal-on-launch normalizers
			// ─────────────────────────────────────────────────────────

			trackHealOnLaunchApplied: (
				templateId: string,
				props: HealOnLaunchAppliedProps
			): void => {
				safeEmit(
					buildPayload(
						templateId,
						AI_OBSERVABILITY_EVENT.HEAL_ON_LAUNCH_APPLIED,
						'A heal-on-launch normalizer touched the template (auto-save or Check Changes)',
						{
							'Normalizer Name': truncate200(props.normalizerName),
							'Items Healed': safeNumber(props.itemsHealed),
							Trigger: truncate200(props.trigger),
						}
					)
				);
			},
		}),
		[safeEmit]
	);
};
